// Research Engine — top navigation bar. Integrates TanStack Router + i18n + auth state.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { UserRole } from "@re/shared";
import { Logo } from "./Logo";
import { Button, Avatar, Badge, cn } from "./primitives";
import { TierBadge } from "./badges";
import { Icon, type IconName } from "./icon";
import { LanguageSwitcher } from "./LanguageSwitcher";

export interface TopBarUser {
  name: string;
  email: string;
  role: UserRole;
}

export function TopBar({ user, onSignOut }: { user: TopBarUser | null; onSignOut: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-border">
      <div className="max-w-[1320px] mx-auto px-6 h-14 flex items-center gap-6">
        <Link to="/" className="flex items-center text-ink hover:opacity-80 transition-opacity">
          <Logo size="md" />
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1 ml-2">
            <NavBtn to="/" icon="compass" label={t("nav.discover")} />
            <NavBtn to="/me" icon="book" label={t("nav.mySessions")} />
          </nav>
        )}

        <div className="flex-1" />

        <LanguageSwitcher />

        {user ? (
          <>
            <Button variant="accent" size="sm" icon="plus" onClick={() => navigate({ to: "/new" })}>
              {t("nav.newResearch")}
            </Button>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 h-9 pl-1 pr-2.5 rounded-full hover:bg-surface-2 transition-colors"
              >
                <Avatar name={user.name} size={28} />
                <Icon name="chevron-down" size={14} className="text-ink-faint" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-surface border border-border rounded-lg overflow-hidden shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)] z-50">
                  <div className="p-3 border-b border-border">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={user.name} size={36} />
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-ink truncate">{user.name}</div>
                        <div className="text-[12px] text-ink-faint truncate">{user.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <TierBadge role={user.role} />
                      {user.role === "admin" && (
                        <Badge tone="line" size="xs" icon="shield">
                          {t("common.admin")}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-1">
                    <MenuItem icon="book" label={t("nav.mySessions")} onClick={() => { setMenuOpen(false); navigate({ to: "/me" }); }} />
                    <MenuItem icon="settings" label={t("nav.settings")} onClick={() => { setMenuOpen(false); navigate({ to: "/settings" }); }} />
                    <MenuItem icon="logout" label={t("common.signOut")} onClick={() => { setMenuOpen(false); onSignOut(); }} />
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <Button variant="primary" size="sm" onClick={() => navigate({ to: "/login" })}>
            {t("common.signIn")}
          </Button>
        )}
      </div>
    </header>
  );
}

function NavBtn({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[13.5px] text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors"
      activeOptions={{ exact: true }}
      activeProps={{ className: "inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[13.5px] text-ink bg-surface-2" }}
    >
      <Icon name={icon} size={14} />
      {label}
    </Link>
  );
}

export function MenuItem({
  icon, label, onClick, danger,
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 h-9 px-2.5 rounded-md text-[13.5px] transition-colors",
        danger ? "text-danger hover:bg-danger-soft" : "text-ink-dim hover:text-ink hover:bg-surface-2",
      )}
    >
      <Icon name={icon} size={15} />
      {label}
    </button>
  );
}
